<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';

interface Props {
  onVerify?: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  onVerify: () => {},
  onExpire: () => {},
  onError: () => {},
});

const config = useRuntimeConfig();
const siteKey = config.public.turnstileSiteKey as string;
const containerRef = ref<HTMLDivElement | null>(null);
let widgetId: string | null = null;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          appearance?: 'always' | 'execute' | 'interaction-only';
          theme?: 'light' | 'dark' | 'auto';
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const renderWidget = () => {
  if (!containerRef.value || !window.turnstile || !siteKey) return;
  if (widgetId !== null) return;

  widgetId = window.turnstile.render(containerRef.value, {
    sitekey: siteKey,
    callback: props.onVerify,
    'expired-callback': props.onExpire,
    'error-callback': props.onError,
    appearance: 'interaction-only',
    theme: 'dark',
  });
};

onMounted(() => {
  if (!siteKey) return;

  if (window.turnstile) {
    renderWidget();
    return;
  }

  const existingScript = document.querySelector('script[src*="turnstile"]');
  if (!existingScript) {
    window.onTurnstileLoad = renderWidget;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    script.async = true;
    document.head.appendChild(script);
  } else {
    window.onTurnstileLoad = renderWidget;
  }
});

onBeforeUnmount(() => {
  if (widgetId !== null && window.turnstile) {
    window.turnstile.remove(widgetId);
    widgetId = null;
  }
});
</script>

<template>
  <div v-if="siteKey" ref="containerRef"></div>
</template>
